import dayjs from "dayjs";
import { getOptionsFromRefList } from "@/util/common_function";

// Mirrors legacy ScheduledEventsInmateTracking.asp / BookingInmateMovementCommon.vbs:
// a recurring Scheduled Event keeps exactly one Pending Loan movement linked at a time.
// The first one is created when the event itself is saved (see ScheduledEventsDetails.jsx);
// this module generates each subsequent one, the moment the previous linked movement is
// resolved (Complete/Cancelled) — legacy's HandleMovementForRecurringEventWithBreakLink.

export const DAYS_OF_WEEK = [
  "Sunday", "Monday", "Tuesday", "Wednesday",
  "Thursday", "Friday", "Saturday",
];

const RESOLVED_MOVEMENT_STATUSES = ["Complete", "Cancelled"];

// DaysOfWeek ref-list IDs are arbitrary (e.g. 54260001), not 0-6 — build the two-way
// lookup once per call so day names <-> ref-list IDs can be resolved against
// scheduledEventsRecurringSchedule[].dayofWeekID.
const getDayOfWeekMaps = () => {
  const daysOfWeekOptions = getOptionsFromRefList("DaysOfWeek", false, false, true);
  const idToName = daysOfWeekOptions.reduce((acc, { value, label }) => {
    acc[value] = label;
    return acc;
  }, {});
  const nameToId = (dayName) => {
    const entry = Object.entries(idToName).find(([, name]) => name === dayName);
    return entry ? parseInt(entry[0], 10) : null;
  };
  return { idToName, nameToId };
};

export const getDayOfWeekID = (dayName) => getDayOfWeekMaps().nameToId(dayName);

// Mirrors ScheduledEventsInmateTracking.asp's GetScheduleByDayOfWeek.
const getActiveScheduleRowForJsDay = (scheduleRows, jsDay, nameToId) => {
  const dayOfWeekID = nameToId(DAYS_OF_WEEK[jsDay]);
  if (dayOfWeekID == null) return null;
  return (
    (scheduleRows || []).find(
      (row) => row?.active && parseInt(row.dayofWeekID, 10) === dayOfWeekID,
    ) || null
  );
};

// Mirrors ScheduledEventsInmateTracking.asp's HandleMovementForRecurringEvent: finds the
// next Active day-of-week on/after today (or Start_Date, or the day after
// Last_Scheduled_Date — whichever rule applies), stopping at End_Date. Returns
// { date, startTime, endTime } (dayjs values) or null if nothing qualifies — legacy's
// silent no-op when no Active day is found, or the event is inactive/expired.
export const findNextOccurrence = (scheduledEvent) => {
  const recurring = scheduledEvent?.scheduledEventsRecurring?.[0];
  if (!recurring?.active) return null;

  const scheduleRows = recurring.scheduledEventsRecurringSchedule || [];
  if (!scheduleRows.length) return null;

  const { nameToId } = getDayOfWeekMaps();

  const startDate = recurring.startDate ? dayjs(recurring.startDate, "MM/DD/YYYY") : null;
  const endDate = recurring.endDate ? dayjs(recurring.endDate, "MM/DD/YYYY") : null;
  const lastScheduledDate = recurring.lastScheduledDate
    ? dayjs(recurring.lastScheduledDate, "MM/DD/YYYY")
    : null;

  const today = dayjs().startOf("day");

  let currentDate = startDate && startDate.isAfter(today, "day") ? startDate : today;
  if (lastScheduledDate && !lastScheduledDate.isBefore(currentDate, "day")) {
    currentDate = lastScheduledDate.add(1, "day");
  }

  for (let i = 0; i < 7; i++) {
    const row = getActiveScheduleRowForJsDay(scheduleRows, currentDate.day(), nameToId);
    if (row) {
      if (endDate && currentDate.isAfter(endDate, "day")) return null;
      return {
        date: currentDate,
        startTime: dayjs(row.startTime, "HH:mm"),
        endTime: dayjs(row.endTime, "HH:mm"),
      };
    }
    currentDate = currentDate.add(1, "day");
  }
  return null;
};

// Mirrors ScheduledEventsInmateTracking.asp's SynchUpWithInmateTracking field-building.
// Reuses the exact movement shape/field names ScheduledEventsDetails.jsx already builds
// for the first occurrence (houseTrackData) so both are identical records.
export const buildMovementFromOccurrence = (scheduledEvent, occurrence, context = {}) => {
  const { date, startTime, endTime } = occurrence;
  const travelTime = Number(scheduledEvent?.travelTime) || 0;

  let durationMinutes = endTime.diff(startTime, "minute");
  if (durationMinutes < 0) durationMinutes += 24 * 60;

  const baseStart = dayjs(
    `${date.format("MM/DD/YYYY")} ${startTime.format("HH:mm")}`,
    "MM/DD/YYYY HH:mm",
  );
  const actualStart = baseStart.subtract(travelTime, "minute");
  const actualExpectedBack = baseStart.add(durationMinutes + travelTime, "minute");

  const scheduledEventOptions = getOptionsFromRefList("Scheduled_Event_Type", false, false, true);
  const loanType = scheduledEventOptions.find(
    (opt) => opt.value === scheduledEvent?.scheduledEventTypeID,
  )?.label;

  const comments = [scheduledEvent?.eventDescription, scheduledEvent?.comments]
    .filter(Boolean)
    .join(" ");

  return {
    actualDate: null,
    actualTime: null,
    approvingOfficerStaffID: context.approvingOfficerStaffID ?? null,
    comments: comments || null,
    expectedBackDate: actualExpectedBack.format("MM/DD/YYYY"),
    expectedBackTime: actualExpectedBack.format("HH:mm"),
    scheduledDate: actualStart.format("MM/DD/YYYY"),
    scheduledTime: actualStart.format("HH:mm"),
    inmateLocation: "Loan",
    loanType,
    movementStatus: "Pending",
    bookingId: context.bookingId ?? null,
    moveReason: "Routine Move",
    loanDestination: "others - See comments",
  };
};

// ScheduledEvents.InmateTrackingID is the FK to the currently-linked movement (legacy's
// dynamic link "\Bookings\InmateTracking"); the nested nrray mirrors the same link and is
// checked as a fallback in case a caller only populated one side.
export const findParentScheduledEvent = (movement, scheduledEvents) => {
  if (!movement?.inmateTrackingID) return null;
  return (
    (scheduledEvents || []).find(
      (se) =>
        se?.inmateTrackingID === movement.inmateTrackingID ||
        se?.inmateTracking?.[0]?.inmateTrackingID === movement.inmateTrackingID,
    ) || null
  );
};

// Housing & Tracking edits (e.g. marking a Loan movement Complete) only ever touch the
// flat bookings.inmateTracking[] array (see BookingsDetails/index.jsx's
// handleSaveHouseTrack) - they never update the nested copy that ScheduledEventsDetails.jsx
// stashed at scheduledEvents[].inmateTracking[0] when the event was created/last edited.
// Left unsynced, both copies of the same InmateTrackingID ride along in the same save
// payload and whichever collection the API writes last wins, so the nested stale "Pending"
// snapshot can silently overwrite a status the user just set to Complete. This keeps the
// nested copy in lock-step with the flat array (the authoritative source) right before the
// payload is sent. Returns a (possibly-unchanged) scheduledEvents array.
export const syncScheduledEventsInmateTracking = (finalBookings) => {
  const scheduledEvents = finalBookings?.scheduledEvents;
  if (!scheduledEvents?.length) return scheduledEvents;

  const trackingByID = new Map(
    (finalBookings?.inmateTracking || [])
      .filter((t) => t?.inmateTrackingID)
      .map((t) => [t.inmateTrackingID, t]),
  );
  if (!trackingByID.size) return scheduledEvents;

  let changed = false;
  const synced = scheduledEvents.map((se) => {
    const linked = se?.inmateTracking?.[0];
    const current = linked?.inmateTrackingID
      ? trackingByID.get(linked.inmateTrackingID)
      : null;
    if (!current || current === linked) return se;
    changed = true;
    return { ...se, inmateTracking: [{ ...linked, ...current }] };
  });

  return changed ? synced : scheduledEvents;
};

// Mirrors BookingInmateMovementCommon.vbs's HandleInmateTrackingLoan recurring branch:
// for each Loan movement that just transitioned to Complete/Cancelled in this save (i.e.
// its previous status, from previousBookings, was neither), if it's linked to a recurring
// Scheduled Event, break the link and generate the next occurrence. Returns a
// (possibly-unchanged) scheduledEvents array — the caller patches it into the save payload.
export const getRecurringChainScheduledEvents = (finalBookings, previousBookings) => {
  const scheduledEvents = finalBookings?.scheduledEvents;
  if (!scheduledEvents?.length) return scheduledEvents;

  const previousByID = new Map(
    (previousBookings?.inmateTracking || [])
      .filter((t) => t?.inmateTrackingID)
      .map((t) => [t.inmateTrackingID, t]),
  );

  const triggeringMovements = (finalBookings?.inmateTracking || []).filter((movement) => {
    if (!movement?.inmateTrackingID) return false;
    if (movement.inmateLocation !== "Loan") return false;
    if (!RESOLVED_MOVEMENT_STATUSES.includes(movement.movementStatus)) return false;
    const previous = previousByID.get(movement.inmateTrackingID);
    if (!previous) return false;
    return !RESOLVED_MOVEMENT_STATUSES.includes(previous.movementStatus);
  });

  if (!triggeringMovements.length) return scheduledEvents;

  const patchedByKey = new Map();

  triggeringMovements.forEach((movement) => {
    const scheduledEvent = findParentScheduledEvent(movement, scheduledEvents);
    if (!scheduledEvent || !scheduledEvent.recurring) return;

    const occurrence = findNextOccurrence(scheduledEvent);
    if (!occurrence) return;

    const newMovement = buildMovementFromOccurrence(scheduledEvent, occurrence, {
      approvingOfficerStaffID: movement.approvingOfficerStaffID,
      bookingId: finalBookings?.bookingId,
    });

    const key = scheduledEvent.scheduledEventID ?? scheduledEvent.id;
    patchedByKey.set(key, {
      ...scheduledEvent,
      inmateTrackingID: null,
      inmateTracking: [newMovement],
      scheduledEventsRecurring: [
        {
          ...scheduledEvent.scheduledEventsRecurring[0],
          lastScheduledDate: occurrence.date.format("MM/DD/YYYY"),
        },
      ],
    });
  });

  if (!patchedByKey.size) return scheduledEvents;

  return scheduledEvents.map((se) => {
    const key = se.scheduledEventID ?? se.id;
    return patchedByKey.has(key) ? patchedByKey.get(key) : se;
  });
};
